import type {
  EntryWithLinkData,
  IProductEnrichment
} from '~/lib/ContentfulService';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';

function createHash(obj: Record<string, string[]>): string {
  return Object.entries(obj)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, values]) => `${key}:${values.sort().join(',')}`)
    .join(';');
}

export async function useProductEnrichments(): Promise<
  ComputedRef<IProductEnrichment[]>
> {
  const locale = useLocale();
  const pdpStore = useProductDetailPageStore();
  const logger = useLogger();

  const result = await useAsyncData(
    `product-enrichment-${pdpStore.decoratedProduct?.sku}`,
    async () => {
      try {
        return await $fetch<EntryWithLinkData<IProductEnrichment>[]>(
          '/api/contentful/productEnrichment',
          {
            query: {
              locale: locale.value,
              rules: pdpStore.productEnrichmentRules
            }
          }
        );
      } catch (error) {
        logger.error('failed to load product enrichments', error);
      }
    },
    {
      // We used a simple `useFetch` before but that resulted in rendering the product enrichment twice
      // once the enrichment was re-fetched (seems like a vue bug?). Therefor, this might be considered as
      // workaround.
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-2344
      watch: [() => createHash(pdpStore.productEnrichmentRules)]
    }
  );

  return computed(() => {
    return (
      result.data.value?.map((productEnrichment) =>
        resolveEntry(productEnrichment)
      ) ?? []
    );
  });
}
